interface TokenData {
  token: string;
  expiresAt: number; // Unix timestamp in seconds
  sessionId: string; // UUID for session tracking
}

/**
 * Types for token response from the server
 */
export interface TokenResponse {
  token: string;
  expiresAt: number;
  sessionId: string;
}

// Local storage key
const TOKEN_STORAGE_KEY = 'auth_token_data';

// Check if we're in a browser environment
const isBrowser =
  typeof window !== 'undefined' && typeof localStorage !== 'undefined';

// Token revalidation settings
const REFRESH_BUFFER_SECONDS = 30; // Refresh token 30 seconds before expiry
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let turnstileCallback: (() => Promise<string>) | null = null;
let isRefreshing = false;

// Cache for the extracted encryption key
let cachedEncryptionKey: string | null = null;

// Check if Turnstile verification is enabled (client-side)
export const isTurnstileEnabled = (() => {
  // Force-convert to string to ensure consistent handling
  const value = String(
    import.meta.env.VITE_ENABLE_TURNSTILE_VERIFICATION
  ).toLowerCase();

  // Very explicit check for falsy values
  const isTrue = value === 'true';

  return isTrue;
})();

// Force initialization when module loads in browser
if (typeof window !== 'undefined') {
  // Use setTimeout to run after module initialization
  setTimeout(() => {
    // Only attempt automatic token fetch if there's no valid token
    // AND Turnstile is explicitly disabled.
    if (!hasValidToken() && !isTurnstileEnabled) {
      initializeAuthToken('turnstile-disabled').catch((error) => {
        console.error('[AuthService] Auto token request failed:', error);
      });
    }
  }, 1000);
}

/**
 * Extract the encryption key from the JWT token without using a JWT library
 * Uses simple base64 decoding to access the payload
 * @returns The encryption key or null if not found
 */
export function getEncryptionKeyFromToken(): string | null {
  // If we have a cached key, return it
  if (cachedEncryptionKey) {
    return cachedEncryptionKey;
  }

  // Get the current token
  const token = getAuthToken();
  if (!token) return null;

  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // The payload is the second part (base64url encoded)
    const payload = parts[1];

    // Convert base64url to base64 by replacing characters and adding padding
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if needed
    const paddedBase64 = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    );

    // Decode the base64 string to a JSON string
    const jsonPayload = atob(paddedBase64);

    // Parse the JSON string to an object
    const payloadObj = JSON.parse(jsonPayload);

    // Extract the encryption key
    const encryptionKey = payloadObj.encryptionKey;

    // Cache the key for future use (until token changes)
    if (encryptionKey) {
      cachedEncryptionKey = encryptionKey;
    }

    return encryptionKey || null;
  } catch (error) {
    console.error(
      '[AuthService] Error extracting encryption key from token:',
      error
    );
    return null;
  }
}

/**
 * Clear the cached encryption key
 * Should be called when the token changes
 */
function clearCachedEncryptionKey(): void {
  cachedEncryptionKey = null;
}

/**
 * Generate a UUID v4
 * @returns {string} UUID v4 string
 */
function generateUUID(): string {
  // Simple UUID v4 implementation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Get token data from storage
function getTokenDataFromStorage(): TokenData | null {
  if (!isBrowser) return null;

  try {
    const storedData = localStorage.getItem(TOKEN_STORAGE_KEY);

    if (!storedData) return null;

    const tokenData: TokenData = JSON.parse(storedData);
    const now = Math.floor(Date.now() / 1000);
    const isExpired = now >= tokenData.expiresAt;

    // Check if the token has expired
    if (isExpired) {
      // Token expired, but we'll keep the sessionId for revalidation
      const sessionId = tokenData.sessionId;
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      clearCachedEncryptionKey(); // Clear cached key when token expires
      return { token: '', expiresAt: 0, sessionId };
    }

    return tokenData;
  } catch (error) {
    console.error('[AuthService] Error reading token from storage:', error);
    return null;
  }
}

// Save token data to storage
function saveTokenToStorage(tokenData: TokenData): void {
  if (!isBrowser) return;

  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));

    // Clear the cached encryption key when the token changes
    clearCachedEncryptionKey();

    // Schedule token refresh
    scheduleTokenRefresh(tokenData);

    // Dispatch an event that authentication succeeded
    window.dispatchEvent(
      new CustomEvent('auth:success', {
        detail: { timestamp: Date.now() },
      })
    );
  } catch (error) {
    console.error('[AuthService] Error saving token to storage:', error);
  }
}

/**
 * Schedule a timer to refresh the token before it expires
 */
function scheduleTokenRefresh(tokenData: TokenData): void {
  if (!isBrowser || !turnstileCallback) return;

  // Clear any existing timer
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  // Calculate refresh time (expiry time minus buffer)
  const now = Math.floor(Date.now() / 1000);
  const timeUntilRefresh = Math.max(
    0,
    tokenData.expiresAt - REFRESH_BUFFER_SECONDS - now
  );

  // Set timer to refresh token before expiry
  refreshTimer = setTimeout(() => {
    refreshAuthToken();
  }, timeUntilRefresh * 1000);
}

/**
 * Refresh the auth token
 */
export async function refreshAuthToken(): Promise<boolean> {
  if (!turnstileCallback || isRefreshing) return false;

  isRefreshing = true;

  try {
    // Get existing token data to retrieve session ID
    const tokenData = getTokenDataFromStorage();
    if (!tokenData) {
      isRefreshing = false;
      return false;
    }

    // If we have a valid token, we can refresh without Turnstile
    // Otherwise, we need to get a new Turnstile token
    const turnstileToken = hasValidToken()
      ? 'use-current-token'
      : await turnstileCallback();

    // Request a new auth token using the same session ID
    const response = await requestAuthToken(turnstileToken);
    const success = !!response; // Convert to boolean

    isRefreshing = false;
    return success;
  } catch (error) {
    console.error('Error refreshing auth token:', error);
    isRefreshing = false;
    return false;
  }
}

// Check if we have a valid token
export function hasValidToken(): boolean {
  const tokenData = getTokenDataFromStorage();
  return tokenData !== null && tokenData.token !== '';
}

// Get the current token
export function getAuthToken(): string | null {
  const tokenData = getTokenDataFromStorage();
  return tokenData && tokenData.token ? tokenData.token : null;
}

// Get the current session ID
export function getSessionId(): string | null {
  const tokenData = getTokenDataFromStorage();
  return tokenData ? tokenData.sessionId : null;
}

// Get the token expiration date
export function getTokenExpiration(): Date | null {
  const tokenData = getTokenDataFromStorage();
  return tokenData && tokenData.expiresAt
    ? new Date(tokenData.expiresAt * 1000)
    : null;
}

/**
 * Get or generate a session ID
 */
function ensureSessionId(): string {
  // Get existing session ID or generate a new one
  const existingData = getTokenDataFromStorage();
  return existingData?.sessionId || generateUUID();
}

/**
 * Check if the user is authenticated with a valid token
 * @internal - Should only be used within this service. Components should use the auth context instead.
 */
function isAuthenticated(): boolean {
  const tokenData = getTokenDataFromStorage();
  if (!tokenData || !tokenData.token || !tokenData.expiresAt) {
    return false;
  }

  // Check if token is expired
  const now = Math.floor(Date.now() / 1000);
  return tokenData.expiresAt > now;
}

// Request a new token using a Turnstile token
export async function requestAuthToken(
  turnstileToken?: string
): Promise<TokenResponse | null> {
  const endpoint = `/.netlify/functions/get-auth-token`;

  try {
    // Get session ID from storage or generate a new one
    const sessionId = ensureSessionId();

    // Get the current token if we have one (for token refresh)
    const currentToken = getAuthToken();

    // Check if we're using a special marker to indicate token refresh
    const isTokenRefresh =
      turnstileToken === 'use-current-token' && currentToken;

    // Determine the actual token value to send based on whether Turnstile is enabled client-side
    // or if we're refreshing with an existing token
    const tokenToSend = isTokenRefresh
      ? undefined // Don't send turnstileToken when refreshing with currentToken
      : isTurnstileEnabled
        ? turnstileToken
        : 'turnstile-disabled';

    // API request
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-ID': sessionId,
        // If turnstile verification is disabled in the app, send this header
        // This relies on the VITE_ENABLE_TURNSTILE_VERIFICATION env var check during initialization
        ...(!isTurnstileEnabled ? { 'X-Turnstile-Disabled': 'true' } : {}),
      },
      body: JSON.stringify({
        turnstileToken: tokenToSend,
        sessionId,
        // Include the current token if we're refreshing
        ...(isTokenRefresh ? { currentToken } : {}),
      }),
    });

    // Check response
    if (!response.ok) {
      const error = await response.json().catch(() => ({
        error: `HTTP error: ${response.status}`,
      }));
      console.error('[AuthService] Failed to get auth token:', error);
      return null;
    }

    // Parse response
    const data = await response.json();

    // Save token to local storage
    saveTokenToStorage({
      token: data.token,
      expiresAt: data.expiresAt,
      sessionId: data.sessionId || sessionId,
    });

    return data;
  } catch (error) {
    console.error('[AuthService] Error requesting auth token:', error);
    return null;
  }
}

/**
 * Initialize authentication token
 * Either use an existing valid token or request a new one
 */
export async function initializeAuthToken(
  turnstileToken?: string
): Promise<boolean> {
  // Check if we have a valid token already
  if (isAuthenticated()) {
    return true; // Already have a valid token
  }

  // Request a new token
  const tokenResponse = await requestAuthToken(turnstileToken);

  const success = !!tokenResponse;
  if (!success) {
    console.error('[AuthService] Failed to get auth token from server');
  }

  return success; // Return true if we got a token
}

// Clear the stored token
export function clearAuthToken(): void {
  if (!isBrowser) return;

  // Clear refresh timer
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  clearCachedEncryptionKey(); // Clear cached encryption key
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

// Force reset all auth state - useful for debugging and when env vars change
export function forceResetAuth(): void {
  if (!isBrowser) return;

  // Clear timer
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  // Clear callback
  turnstileCallback = null;
  isRefreshing = false;

  // Clear encryption key cache
  clearCachedEncryptionKey();

  // Clear localStorage
  localStorage.removeItem(TOKEN_STORAGE_KEY);

  // Force page reload to ensure all states are reset
  // window.location.reload();
}

/**
 * Set the Turnstile callback function that will be used to get a new Turnstile token
 * when refreshing the auth token
 * @param callback Function that returns a Promise resolving to a Turnstile token string
 */
export function setTurnstileCallback(callback: () => Promise<string>): void {
  turnstileCallback = callback;

  // If we have a valid token, schedule its refresh
  const tokenData = getTokenDataFromStorage();
  if (tokenData && tokenData.token) {
    scheduleTokenRefresh(tokenData);
  }
}

/**
 * Handle an authentication failure by trying to refresh the token and retry the operation
 * @param operation The async function to retry after refreshing the token
 * @returns The result of the operation if successful, null otherwise
 */
export async function handleAuthFailure<T>(
  operation: () => Promise<T>
): Promise<T | null> {
  // Try to refresh the token
  const refreshSuccess = await refreshAuthToken();

  if (refreshSuccess) {
    try {
      // Retry the operation with the new token
      return await operation();
    } catch (error) {
      console.error('Operation failed after token refresh:', error);
      return null;
    }
  } else {
    console.error('Failed to refresh authentication token');
    return null;
  }
}
